import {
  AddOutlined,
  ContentCopy,
  DeleteOutlineOutlined,
  Edit,
  Power,
  PowerOff,
} from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { useSysDataClubsQuery } from "@/api/hooks/sys-data-hooks";
import { useCfgPerm, useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import ApplyAuthGroupsTableControl from "@/components/apply-auth-groups-table-control";
import BulkOperationReportModal from "@/components/bulk-operation-report-modal";
import Popconfirm from "@/components/popcofirm";
import { useTableSettings } from "@/hooks/use-table-settings";
import { assembleNamesFilter, enrichClubManagerWithClubData } from "@/lib/cfg-utils";
import { DATE_FORMAT_WITH_UTC_TAG, ROOM_CONFIGS_NAMES } from "@/lib/constants";
import {
  CmsAggregatorCreateEditModal,
  CmsAggregatorForm,
} from "@/module/room-configs-module/cms-aggregator-create-edit-modal";
import {
  useConfigChangeEnabled,
  useDeleteConfigs,
  useSaveConfig,
  useUpdateConfig,
} from "@/module/room-configs-module/hooks/room-configs-hooks";
import { ResultDtoLong, SysCfgRegularDto } from "@/shared/api";
import { SysCfgCmsAggregator } from "@/types/room-config-types";

const CmsAggregatorConfigTable = ({ room }: { room: string }) => {
  const { t } = useTranslation();
  const {
    data: configsRs,
    isLoading: configsIsLoading,
    refetch,
  } = useSysCfgQuery({
    domain: room,
    config_name: ROOM_CONFIGS_NAMES.CMS_AGGREGATOR,
  });

  const { data: clubConfigsData } = useSysDataClubsQuery({
    domain: room,
  });

  const cfgPerm = useCfgPerm({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.CMS_AGGREGATOR,
  });

  const isOwner = useIsOwnerOrSuperAdmin();

  const roomConfigsSortedData = React.useMemo(() => {
    const data = enrichClubManagerWithClubData(
      configsRs?.data.content ?? [],
      clubConfigsData?.data ?? []
    );
    return (
      data.sort(
        (a, b) => new Date(b.created_at).getDate() - new Date(a.created_at).getDate()
      ) ?? []
    );
  }, [clubConfigsData, configsRs]);

  const [bulkOperationResult, setBulkOperationResult] = useState<ResultDtoLong[] | undefined>(
    undefined
  );

  const { saveConfigMutation: handleClickSaveNewConfig, isPending: isSaveConfigPending } =
    useSaveConfig({
      roomName: room,
      cfgName: ROOM_CONFIGS_NAMES.CMS_AGGREGATOR,
      onSuccessCallback: () => {
        void refetch();
      },
    });

  const { updateConfigMutation: handleClickUpdateConfig, isPending: isUpdateConfigPending } =
    useUpdateConfig({
      roomName: room,
      cfgName: ROOM_CONFIGS_NAMES.CMS_AGGREGATOR,
      onSuccessCallback: () => {
        void refetch();
      },
    });

  const handleChangeEnableBulk = useConfigChangeEnabled({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.CMS_AGGREGATOR,
    onSuccessCallback: (table) => {
      void refetch();
      table.resetRowSelection();
    },
  });

  const handleDeleteConfigs = useDeleteConfigs({
    roomName: room,
    cfgName: ROOM_CONFIGS_NAMES.CMS_AGGREGATOR,
    onSuccessCallback: (table, response) => {
      setBulkOperationResult([response.data]);
      void refetch();
      table.resetRowSelection();
    },
  });

  const [modalMode, setModalMode] = useState<"edit" | "add" | undefined>(undefined);
  const [modalInitValues, setModalInitValues] = useState<SysCfgRegularDto | undefined>(
    undefined
  );
  const [editItemName, setEditItemName] = useState<string>("");

  function handleClickAddConfig() {
    setModalInitValues(undefined);
    setEditItemName("");
    setModalMode("add");
  }

  const { defaultProps, state, columnFilters, grouping, globalFilter } =
    useTableSettings<SysCfgCmsAggregator>("cmsAggregator");

  const columns = useMemo<MRT_ColumnDef<SysCfgCmsAggregator>[]>(
    () => [
      {
        header: t("cmsAggregatorTable.enable"),
        filterFn: "contains",
        enableEditing: false,
        accessorFn: (row) => row.enabled ?? "-",
        size: 80,
        Cell: ({ row }) => <Checkbox checked={row.original.enabled} disabled />,
      },
      {
        header: t("cmsAggregatorTable.name"),
        accessorFn: (row) => row.name ?? "-",
        filterFn: "contains",
      },

      {
        header: t("cmsAggregatorTable.username"),
        accessorFn: (row) => row.data?.username ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.gameGroup"),
        accessorFn: (row) => row.data?.game_group ?? "-",
        filterFn: "contains",
        size: 120,
      },
      {
        header: t("cmsAggregatorTable.cid"),
        accessorFn: (row) => row.data?.cid ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.aid"),
        accessorFn: (row) => row.data?.aid ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.isGold"),
        accessorFn: (row) => row.data?.is_gold?.toString() ?? "-",
        filterFn: "contains",
      },

      {
        header: t("cmsAggregatorTable.accountGems"),
        accessorFn: (row) => row?.cmsAggregatorRedisInfo?.account_gems ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.clubGems"),
        accessorFn: (row) => row?.cmsAggregatorRedisInfo?.club_gems ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.goldBalance"),
        accessorFn: (row) => row.cmsAggregatorRedisInfo?.gold_balance ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.alcBalance"),
        accessorFn: (row) => row.cmsAggregatorRedisInfo?.alc_balance ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.alcIssued"),
        accessorFn: (row) => row.cmsAggregatorRedisInfo?.alc_issued ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.clubLevel"),
        accessorFn: (row) => row.cmsAggregatorRedisInfo?.club_level ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.currMems"),
        accessorFn: (row) => row.cmsAggregatorRedisInfo?.cur_members ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.maxMems"),
        accessorFn: (row) => row.cmsAggregatorRedisInfo?.max_members ?? "-",
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.updatedAt"),
        accessorFn: (row) => row.cmsAggregatorRedisInfo?.updated_at ?? "-",
        Cell: ({ row }) => {
          return moment
            .unix(Number(row.original.cmsAggregatorRedisInfo?.updated_at) / 1000)
            .utc(false)
            .format(DATE_FORMAT_WITH_UTC_TAG);
        },
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.nlhExportTimestamp"),
        accessorFn: (row) => row.data?.nlh_export_timestamp ?? "-",
        Cell: ({ row }) => {
          return moment
            .unix(Number(row.original.data?.nlh_export_timestamp) / 1000)
            .utc(false)
            .format(DATE_FORMAT_WITH_UTC_TAG);
        },
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.ploExportTimestamp"),
        accessorFn: (row) => row.data?.plo_export_timestamp ?? "-",
        Cell: ({ row }) => {
          return moment
            .unix(Number(row.original.data?.plo_export_timestamp) / 1000)
            .utc(false)
            .format(DATE_FORMAT_WITH_UTC_TAG);
        },
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.nlhsExportTimestamp"),
        accessorFn: (row) => row.data?.nlhs_export_timestamp ?? "-",
        Cell: ({ row }) => {
          return moment
            .unix(Number(row.original.data?.nlhs_export_timestamp) / 1000)
            .utc(false)
            .format(DATE_FORMAT_WITH_UTC_TAG);
        },
        filterFn: "contains",
      },
      {
        header: t("cmsAggregatorTable.ofcExportTimestamp"),
        accessorFn: (row) => row.data?.ofc_export_timestamp ?? "-",
        Cell: ({ row }) => {
          return moment
            .unix(Number(row.original.data?.ofc_export_timestamp) / 1000)
            .utc(true)
            .format(DATE_FORMAT_WITH_UTC_TAG);
        },
        filterFn: "contains",
      },
    ],
    [t]
  );

  const table = useMaterialReactTable({
    data: roomConfigsSortedData ?? [],
    columns: columns,
    getRowId: (originalRow) => originalRow.id,
    selectAllMode: "all",
    state: {
      isLoading: configsIsLoading,
      ...state,
      sorting: state.sorting.length > 0 ? state.sorting : [{ id: "name", desc: false }],
    },
    ...defaultProps,
    initialState: {
      pagination: { pageSize: 100, pageIndex: 0 },
      density: "compact",
      sorting: [{ id: "name", desc: false }],
    },
    enableRowActions: true,
    enableSelectAll: true,
    enableRowSelection: true,
    enableGrouping: true,
    enableColumnDragging: false,
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    enablePagination: true,
    enableSorting: false,
    muiTableHeadCellProps: {
      sx: {
        verticalAlign: "middle",
        "& *": {
          whiteSpace: "normal",
          lineHeight: 1.35,
        },
      },
    },
    defaultColumn: {
      enableResizing: false,
      grow: false,
      maxSize: 30,
      minSize: 10,
    },

    renderTopToolbarCustomActions: ({ table }) => {
      const selectedCount = table.getSelectedRowModel().rows.length;

      return (
        <div style={{ height: "40px", display: "flex", alignItems: "center" }}>
          {cfgPerm.write && (
            <Button onClick={() => handleClickAddConfig()}>
              <AddOutlined /> {t("new")}
            </Button>
          )}

          {selectedCount > 0 && (
            <div style={{ marginLeft: 30, display: "flex" }}>
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="success"
                  onClick={() => handleChangeEnableBulk(table, true)}
                >
                  <Power /> {t("enable")}
                </IconButton>
              )}
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="error"
                  onClick={() => handleChangeEnableBulk(table, false)}
                >
                  <PowerOff /> {t("disable")}
                </IconButton>
              )}

              {cfgPerm.delete && (
                <Popconfirm
                  title={t("deleteItems")}
                  description={t("areYouSureToDeleteItems")}
                  onConfirm={() => handleDeleteConfigs(table)}
                >
                  <IconButton size="small" style={{ fontSize: "15px" }} color="error">
                    <DeleteOutlineOutlined /> {t("delete")}
                  </IconButton>
                </Popconfirm>
              )}

              {isOwner && (
                <ApplyAuthGroupsTableControl
                  domain={room}
                  cfgName={ROOM_CONFIGS_NAMES.CMS_AGGREGATOR}
                  table={table}
                  filters={assembleNamesFilter(table)}
                  totalElementsCount={selectedCount}
                  onSuccess={() => void refetch()}
                />
              )}
            </div>
          )}
        </div>
      );
    },
    renderRowActions: ({ row }) => (
      <Box>
        <IconButton
          onClick={() => {
            setModalInitValues({
              ...row.original,
              auth_groups: new Set(row.original.auth_groups),
            });
            setModalMode("edit");
            setEditItemName(row.original.name);
          }}
        >
          <Edit />
        </IconButton>

        {cfgPerm.write && (
          <IconButton
            onClick={() => {
              setModalInitValues({
                ...row.original,
                data: { ...row.original.data, comment: "" },
                name: "",
                enabled: false,
              });
              setModalMode("add");
            }}
          >
            <ContentCopy />
          </IconButton>
        )}
      </Box>
    ),
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [columnFilters, grouping, globalFilter, table]);

  return (
    <>
      <MaterialReactTable table={table} />

      <CmsAggregatorCreateEditModal
        open={!!modalMode}
        mode={modalMode}
        initialData={modalInitValues as CmsAggregatorForm}
        onSave={(data) => {
          if (modalMode === "add") return handleClickSaveNewConfig(data);
          if (modalMode === "edit") return handleClickUpdateConfig(editItemName, data);
        }}
        onCancel={() => {
          setModalMode(undefined);
          setModalInitValues(undefined);
          setEditItemName("");
        }}
      />

      {bulkOperationResult && (
        <BulkOperationReportModal
          resultList={bulkOperationResult}
          title={t("operationResult")}
          showProblematic
          showSuccess
          onCancel={() => setBulkOperationResult(undefined)}
          open={!!bulkOperationResult}
        />
      )}
    </>
  );
};

export default CmsAggregatorConfigTable;
